import { useEffect, useRef, useState } from 'react';
import { dragDialogTo, unzoom } from './floatingWindow';

// The one dialog. Every popup form goes through here so that none of them can
// grow taller than the window and lose its close button: the panel is capped
// to the overlay, the header (with the × button) and footer stay pinned, only
// the body scrolls, and Escape closes it. tests/dialogs-use-shared-modal.test.mjs
// fails the build if a page builds its own overlay instead.
//
// Sizing rules, each one learned the hard way:
// - The panel is capped with max-h-full against the overlay, not with vh. On a
//   phone 100vh is the tallest viewport (browser toolbar hidden), so a 90vh
//   panel was taller than the visible screen while the toolbar showed, and the
//   PLOW cell editor opened with its × above the top edge. The overlay is
//   `fixed inset-0`, which is always exactly the visible area.
// - The panel centres itself with auto margins rather than the overlay using
//   items-center. Centring a too-tall item cuts both its ends off with no way
//   to scroll to the top; auto margins collapse to zero when the item
//   overflows, and the overlay scrolls, so the header is always reachable.
// - The title wraps (min-w-0 break-words) and the × never shrinks, so a long
//   reference or email address in a title cannot push the button off the panel.
// - The header is a drag handle, like the suggestions window's, so a dialog can
//   be moved off whatever it is about. It keeps an offset from its centred
//   place (a fresh one opens centred again) and stays wholly on screen.
//
// `dataTour` / `closeDataTour` put [data-tour] anchors on the panel and the
// close button for guided tours that spotlight or dismiss a dialog.
export default function Modal({ title, children, onClose, footer, wide, xl, dataTour, closeDataTour }) {
  const closeRef = useRef(null);
  const panelRef = useRef(null);

  // Page dialogs sit inside the zoomed `.content`, so the pointer and the
  // screen are converted into the panel's zoomed units, which the offset is in.
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const dragStart = useRef(null);
  const startDrag = (e) => {
    if (e.button !== 0 || e.target.closest('button, a, input, select, textarea')) return;
    const r = panelRef.current.getBoundingClientRect();
    const zoom = r.width / panelRef.current.offsetWidth || 1;
    dragStart.current = { zoom, offset, pointer: unzoom({ x: e.clientX, y: e.clientY }, zoom),
      rect: unzoom({ left: r.left, right: r.right, top: r.top, bottom: r.bottom }, zoom) };
    e.currentTarget.setPointerCapture?.(e.pointerId);
    e.preventDefault();
  };
  const moveDrag = (e) => {
    const s = dragStart.current;
    if (s) setOffset(dragDialogTo(s, unzoom({ x: e.clientX, y: e.clientY }, s.zoom), unzoom({ width: window.innerWidth, height: window.innerHeight }, s.zoom)));
  };
  const endDrag = () => { dragStart.current = null; };

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  // Development canary: if, despite the rules above, the close button is not
  // inside the viewport once the dialog has laid out, say so in the console.
  // A dialog whose × cannot be reached is a page reload for whoever hits it.
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    const check = () => {
      const r = closeRef.current?.getBoundingClientRect();
      if (!r) return;
      const inside = r.top >= 0 && r.left >= 0 && r.bottom <= window.innerHeight && r.right <= window.innerWidth;
      if (!inside) console.warn('Modal: the close button is off screen', { title, rect: r });
    };
    const id = requestAnimationFrame(check);
    window.addEventListener('resize', check);
    return () => { cancelAnimationFrame(id); window.removeEventListener('resize', check); };
  }, [title]);

  return (
    <div className="fixed inset-0 bg-black/30 flex justify-center overflow-y-auto z-50 p-2 sm:p-4">
      {/* Flex column so the header + footer stay pinned and only the body scrolls —
          long forms never hide the Save/Cancel buttons below the fold. */}
      <div
        role="dialog"
        aria-modal="true"
        ref={panelRef}
        data-tour={dataTour}
        className="bg-white rounded-lg shadow-xl w-[96%] sm:w-[92%] max-h-full my-auto flex flex-col animate-fade-in"
        style={{ maxWidth: xl ? 'min(1100px, 96vw)' : wide ? 'min(720px, 96vw)' : 'min(540px, 96vw)', translate: `${offset.x}px ${offset.y}px` }}
      >
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-200 shrink-0 cursor-move select-none touch-none"
          onPointerDown={startDrag} onPointerMove={moveDrag} onPointerUp={endDrag} onPointerCancel={endDrag} title="Drag to move">
          <h3 className="text-sm font-bold text-navy min-w-0 break-words">{title}</h3>
          <button ref={closeRef} data-tour={closeDataTour} onClick={onClose} aria-label="Close" className="shrink-0 text-gray-400 hover:text-gray-600 text-lg leading-none">&times;</button>
        </div>
        <div className="px-4 py-3 overflow-y-auto flex-1 min-h-0">{children}</div>
        {footer && (
          <div className="flex justify-end gap-2 px-4 py-2 border-t border-gray-200 shrink-0 bg-white rounded-b-lg">{footer}</div>
        )}
      </div>
    </div>
  );
}
